/**
 * Keeps secret-bearing URLs out of everything this app sends to Bayawatch.
 *
 * Four routes carry a bearer secret in the PATH (`routes/routes.tsx`):
 * `/reset_password/:token`, `/public/issues/:token`, `/public/reports/:token`
 * and `/public/reports/:token/issues/:issueId`. Whoever holds the token can
 * reset the password or read the shared issue/report, so the token must not
 * reach a third system — and `@bayawatch/sdk` 0.2.0 stamps
 * `window.location.href` on every error event and every session-replay batch.
 *
 * KEEP IN SYNC with `redactUrl` in `scripts/engineFloorGate.js`, an ES5 copy
 * (that file is inlined into index.html before any module loads, so it cannot
 * import this one). `bayawatchPrivacy.test.ts` runs both implementations over
 * the same cases, so a drift between them fails the suite.
 */

import { isReplaySafePage } from '@/lib/monitoring/sessionReplayGate'

// react-router matches paths case-insensitively and decodes every path segment
// before matching (`decodePath` in @remix-run/router), so `/Public/%72eports/<t>`
// renders the shared report exactly like `/public/reports/<t>` — the match runs
// on the decoded text with the `i` flag. Repeated slashes are accepted as a
// harmless superset. The match is not anchored, so `/api/...` spellings inside
// error messages are covered too. In a URL only the token segment is replaced
// (`/issues/42` after it is kept); in free text there is no reliable end of the
// URL, so everything after the token up to whitespace or a quote goes with it.
const SECRET_PATH = /(\/+(?:reset_password|public\/+issues|public\/+reports))\/+[^/?#\s]+/i
const SECRET_IN_PATH = /(\/+(?:reset_password|public\/+issues|public\/+reports))\/+[^/?#\s]+/gi
// Skips a token segment that is already `:token`: `filterBayaWatchPayload`
// (lib/monitoring/httpFailureReporting.ts) runs first and redacts the current
// page's secret by value, keeping the rest of the URL (`/issues/5`) readable.
const SECRET_IN_TEXT =
  /(\/+(?:reset_password|public\/+issues|public\/+reports))\/+(?!:token(?:[/?#]|$))[^\s"'<>`]+/gi

export const REDACTED = ':token'

/** `decodeURI` keeps `%2F` encoded, as the router does; malformed input stays raw. */
function decodeForMatching(text: string): string {
  try {
    return decodeURI(text)
  } catch {
    return text
  }
}

function replaceSecrets(text: string, pattern: RegExp): string {
  const decoded = decodeForMatching(text)
  if (!SECRET_PATH.test(decoded)) return text
  const redacted = decoded.replace(pattern, `$1/${REDACTED}`)
  return redacted === decoded ? text : redacted
}

// A whitespace/quote-delimited run: the unit free text is decoded in, so text
// around a secret path (other stack frames, `my%20chunk.js`) stays byte-exact.
const TEXT_RUN = /[^\s"'<>`]+/g

/**
 * Replace every secret path — and whatever URL text follows it — inside free
 * text (messages, stacks). Text without a secret path is returned unchanged.
 */
export function redactSecretPaths(text: string): string {
  return text.replace(TEXT_RUN, run => replaceSecrets(run, SECRET_IN_TEXT))
}

/**
 * True when the path carries a secret, i.e. the page must not be recorded.
 * Fails CLOSED: a location that is not a string counts as secret.
 */
export function isSecretBearingPath(pathname: unknown): boolean {
  if (typeof pathname !== 'string') return true
  return SECRET_PATH.test(decodeForMatching(pathname))
}

/**
 * Redact a URL for telemetry: secret path segments become `:token`; on a secret
 * page the whole query becomes `?redacted`, elsewhere query VALUES become
 * `redacted` (keys are kept, they help diagnose things like the `?_reload`
 * loop guard); a fragment always collapses to `#redacted`.
 */
export function redactUrl(href: string): string {
  let rest = String(href)
  let fragment = ''
  const hashAt = rest.indexOf('#')
  if (hashAt !== -1) {
    fragment = '#redacted'
    rest = rest.slice(0, hashAt)
  }
  let rawQuery: string | null = null
  const queryAt = rest.indexOf('?')
  if (queryAt !== -1) {
    rawQuery = rest.slice(queryAt + 1)
    rest = rest.slice(0, queryAt)
  }
  let query = ''
  if (rawQuery !== null && isSecretBearingPath(rest)) {
    query = '?redacted'
  } else if (rawQuery !== null) {
    const keys = rawQuery
      .split('&')
      .filter(pair => pair !== '')
      .map(pair => {
        const eq = pair.indexOf('=')
        return eq === -1 ? pair : `${pair.slice(0, eq)}=redacted`
      })
    query = keys.length ? `?${keys.join('&')}` : ''
  }
  return replaceSecrets(rest, SECRET_IN_PATH) + query + fragment
}

type BayawatchEventLike = {
  url?: unknown
  message?: unknown
  stack?: unknown
}

/**
 * The SDK's `beforeSend`: scrub the event in place and return it. The SDK
 * merges the return value back with `Object.assign`, so every field that can
 * carry the page URL is rewritten here — `url` always, `message` and `stack`
 * because an error text can quote a request path (`/api/public/reports/<t>`).
 */
export function redactBayawatchEvent<T extends BayawatchEventLike>(event: T): T {
  if (typeof event.url === 'string') event.url = redactUrl(event.url)
  if (typeof event.message === 'string') event.message = redactSecretPaths(event.message)
  if (typeof event.stack === 'string') event.stack = redactSecretPaths(event.stack)
  return event
}

/**
 * Call BEFORE an in-app navigation whose target is not known to be ordinary —
 * today the native push / universal-link deep links (`lib/deepLinkNavigation.ts`).
 * `bayawatch.ts` decides once, when the SDK loads, whether to record; a session
 * that started on an ordinary page would keep recording after the SPA moved to
 * a token route, and the SDK stamps the CURRENT `location.href` on every replay
 * batch. So when the target is secret-bearing the SDK is torn down while the
 * old URL is still current: `destroy()` flushes the buffered replay and errors
 * synchronously (payloads are built at flush time) and removes every listener.
 * Monitoring then stays off until the next app launch — a deliberate trade for
 * a rare path (opening a shared link inside the logged-in native app).
 *
 * "Secret" uses the same deny-by-default rule as the load-time replay gate
 * (`isReplaySafePage`), so a target that gate would refuse to record is never
 * recorded by navigating into it either.
 */
export function stopRecordingBeforeSecretNavigation(path: string): void {
  const target = typeof path === 'string' ? `https://placeholder.invalid${path}` : ''
  if (!isSecretBearingPath(path) && isReplaySafePage(target)) return
  try {
    const instance = (window as unknown as { __bayawatch_instance__?: { destroy: () => void } })
      .__bayawatch_instance__
    instance?.destroy()
  } catch {
    // Monitoring is best-effort and must never block navigation.
  }
}
